import React, { useEffect, useState } from 'react';
import { FaArrowCircleRight, FaEdit, FaSave, FaTools } from 'react-icons/fa';
import './index.css';
import { getConditionValue, hoursIn24And12Format, translate, verifyHideCountryItems } from '../../../../utils/Common';
import { useForm } from 'react-hook-form/dist/index.ie11';
import Skeleton from '../../../../components/ui/Skeleton';
import { usePlatforms } from '../../../../hooks/usePlatforms';
import { ToastContainer, useToast } from '../../../../components/common/Toast';
import BtnSync from '../../../../components/BtnSync';
import Button from '../../../../components/common/Button/Button';
import { getLampHoursSetup } from '../../../../config/providers';

const FormSettingsLocker = (props) => {
  const {dataDetails, setOptionSelect, isLoadingData, externalId, setExternalId, typeView, setDataDetails, permissions, optionsProviders } = props;
  const canCreate = !!permissions?.canCreate;
  const canUpdate = !!permissions?.canUpdate;
  const canSyncDetails = !!permissions?.canSyncDetails;
  const canEditThisView = typeView === 'create' ? canCreate : canUpdate;

  const {createSettingsPlatform, updateSettingsPlatform, updateGeneralDataPlatform} = usePlatforms();
  const { register, errors, handleSubmit, reset } = useForm();
  const { toasts, showSuccess, showError, removeToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [editMode, setEditMode] = useState(true);

  const formFields = [];

  // horas de lámpara: obligatorias en Outdoor; Mercury no las lleva
  const { hidden: isLampHidden, required: isLampRequired } = getLampHoursSetup(dataDetails?.IdFabricante, dataDetails?.IdTypeLocker, optionsProviders);

  const selectFields = [
    { id: 'screenId',      label: translate("global.screenResolution"), required: true,  options: [{id: 1, value:'Square'}, {id: 2, value:'Portrait'}, {id: 5, value:'Portrait BR'}], colSize: 'col-span-12 md:col-span-6', valueDefault: dataDetails?.resolutionScreen, disabled: (dataDetails?.resolutionScreen && editMode) },
    { id: 'statusId',      label: translate("global.status"),           required: true,  options: [{id: 1, value: translate("global.status.locker.active")}, {id: 2, value: translate("global.status.locker.inactive")}, {id: 3, value: translate("global.status.locker.uninstalled")}], colSize: 'col-span-12 md:col-span-6', valueDefault: dataDetails?.IdEstatus, isHidden: !dataDetails?.BoxitSettingId, disabled: editMode },
    { id: 'timeTurnLampOn',  label: translate("global.turnLampOnHour"),  required: isLampRequired, options: hoursIn24And12Format, colSize: 'col-span-12 md:col-span-6', valueDefault: dataDetails?.TurnOn,  isHidden: isLampHidden, disabled: dataDetails?.TurnOn && editMode },
    { id: 'timeTurnLampOff', label: translate("global.turnLampOffHour"), required: isLampRequired, options: hoursIn24And12Format, colSize: 'col-span-12 md:col-span-6', valueDefault: dataDetails?.TurnOff, isHidden: isLampHidden, disabled: dataDetails?.TurnOff && editMode },
    { id: 'checkItem',     label: translate("global.checkItem"),         required: true,  options: [{id: true, value: translate("global.enabled")}, {id: false, value: translate("global.disabled")}], colSize: 'col-span-12 md:col-span-6', valueDefault: dataDetails?.checkItem, isHidden: !verifyHideCountryItems(["br"]), disabled: (getConditionValue(dataDetails?.checkItem) && editMode) },
  ];

  const renderInputField = (props) => {
    const { id, label, required, colSize = "col-span-12", valueDefault = "", disabled } = props;

    return (
      <div className={colSize}>
        {isLoadingData ? <div className='mt-1 mb-2'><Skeleton width={100} height={10} /></div> : <label className="text-label-title" htmlFor={id}>
            {label} {required && <span className='text-destructive'>*</span>}
          </label>}
        <div className="mb-3 mb-2">
          {isLoadingData ? <div className='mt-2 mb-3'><Skeleton width='100%' height={40} /></div> : (
            <input
            id={id}
            name={id}
            defaultValue={valueDefault}
            disabled={disabled} 
            ref={register({
              required: required && {
                value: true,
                message: translate('global.isRequerid'),
              },
            })}
            className={`h-11 w-full rounded-lg border border-transparent bg-muted/60 px-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:border-primary focus-visible:bg-background focus-visible:ring-4 focus-visible:ring-primary/15 disabled:bg-transparent disabled:text-muted-foreground disabled:cursor-not-allowed form-control-md${errors[id] ? ' mb-2 border-destructive' : ''}`}
          />
          )}
          
          {errors[id] && <div className="mt-1 text-xs font-medium text-destructive">{errors[id].message}</div>}
        </div> 
      </div>
    );
  };
  
  const renderSelectField = (props) => {
    const { id, label, required, options, colSize = "col-span-12", valueDefault = "", isHidden, disabled} = props;

    if (isHidden) return null;

    return (
      <div className={colSize}>
        <div className="mb-3 mb-2">
          {isLoadingData ? (
            <div className="mt-1 mb-2"><Skeleton width={100} height={10} /></div>
          ) : (
            <label className="text-label-title" htmlFor={id}>
              {label} {required && <span className="text-destructive">*</span>}
            </label>
          )}
          <div className="mb-3">
            {isLoadingData ? (
              <div className="mt-2 mb-3"><Skeleton width="100%" height={40} /></div>
            ) : (
              <select
                id={id}
                name={id}
                defaultValue={valueDefault}
                disabled={disabled}
                ref={register({
                  required: required && {
                    value: true,
                    message: translate('global.isRequerid'),
                  },
                })}
                className={`h-11 w-full rounded-lg border border-transparent bg-muted/60 px-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:border-primary focus-visible:bg-background focus-visible:ring-4 focus-visible:ring-primary/15 disabled:bg-transparent disabled:text-muted-foreground disabled:cursor-not-allowed form-control-md${errors[id] ? ' mb-2 border-destructive' : ''}`}
              >
                <option value="">{translate("global.select")}...</option>
                {options.map((option, index) => (
                  <option key={index} value={option.id ?? index}>
                    {option?.value}
                  </option>
                ))}
              </select>
            )}
          </div>
          {errors[id] && <div className="mt-1 text-xs font-medium text-destructive">{errors[id].message}</div>}
        </div>
      </div>);
  };

  const submitCreateSettingsPlatform = async (data) => { 
    setIsLoading(true);
    const abortController = new AbortController();
  
    const dataToSend = {
      ...data,
      screenId: parseInt(data.screenId),
      timeTurnLampOn: data.timeTurnLampOn || undefined, // opcionales u ocultas: vacío queda NULL
      timeTurnLampOff: data.timeTurnLampOff || undefined,
      locationId: dataDetails?.externalId,
      splashTimer: 90000,
      supportNumber: "718-374-6835",
      supportEmail: "support@golocker.com", 
      boxValidationTimer: 30000, 
      checkItem: data.checkItem==="true" ? true : false,
      currentTemperature: 28,
      tempUpperLimit: 30,
      tempLowerLimit: 27, 
      fanStatus: false,
      appName: "SF.GrupoAuraBoxIt.exe",
      appValidationTimer: 10000,
      appValidationTimeout: 30000,
    }

    // Guardar coordenadas si se ingresaron
    if (data.coordinates) {
      const generalPayload = {
        locationName: dataDetails?.namePlatform || '',
        address: dataDetails?.fisicAddress || '',
        city: dataDetails?.city || '',
        generation: Number(dataDetails?.IdGeneration),
        statusId: dataDetails?.IdEstatus || 1,
        environmentId: dataDetails?.IdTipoAmbiente,
        locationId: dataDetails?.externalId,
        typeLocker: Number(dataDetails?.IdTypeLocker),
        providerId: Number(dataDetails?.IdFabricante),
        pudoId: dataDetails?.pudoId || null,
        coordinates: data.coordinates,
      };
      await updateGeneralDataPlatform(generalPayload, abortController.signal);
    }

    const response = await createSettingsPlatform(dataToSend, abortController.signal);
    
    setExternalId(null)
    if (response.code === 200) {
      if (response?.boxitSettingId > 0) {
          setExternalId(externalId);
          setOptionSelect('modules');
      }
    } else {
      showError(translate("dashboard.packages.details.dropOff.receivePackage.NOT_FOUND"));
    }
    setIsLoading(false);
  }
  
  const submitUpdateSettingsPlatform = async (data) => {
    setIsLoading(true);
    const abortController = new AbortController();

    const newStatusId = Number(data.statusId);

    // Actualizar datos generales si cambió estatus o coordenadas
    const coordinatesChanged = data.coordinates !== (dataDetails?.coordinates ?? '');
    if ((newStatusId && newStatusId !== dataDetails?.IdEstatus) || coordinatesChanged) {
      const generalPayload = {
        locationName: dataDetails?.namePlatform || '',
        address: dataDetails?.fisicAddress || '',
        city: dataDetails?.city || '',
        generation: Number(dataDetails?.IdGeneration),
        statusId: newStatusId || dataDetails?.IdEstatus,
        environmentId: dataDetails?.IdTipoAmbiente,
        locationId: dataDetails?.externalId,
        typeLocker: Number(dataDetails?.IdTypeLocker),
        providerId: Number(dataDetails?.IdFabricante),
        pudoId: dataDetails?.pudoId || null,
        coordinates: data.coordinates || null,
      };
      await updateGeneralDataPlatform(generalPayload, abortController.signal);
    }
    
    const dataToSend = {
      ...data,
      screenId: parseInt(data.screenId),
      timeTurnLampOn: data.timeTurnLampOn || undefined, // opcionales u ocultas: vacío queda NULL
      timeTurnLampOff: data.timeTurnLampOff || undefined,
      locationId: dataDetails?.externalId,
      splashTimer: 90000,
      supportNumber: "718-374-6835",
      supportEmail: "support@golocker.com", 
      boxValidationTimer: 30000, 
      checkItem: data.checkItem==="true" ? true : false,
      currentTemperature: 28,
      tempUpperLimit: 30,
      tempLowerLimit: 27, 
      fanStatus: false,
      appName: "SF.GrupoAuraBoxIt.exe",
      appValidationTimer: 10000,
      appValidationTimeout: 30000
    }    

    const response = await updateSettingsPlatform(dataToSend, abortController.signal);

    if (response.code === 200) {
      showSuccess(translate("global.updateSuccessSettingsPlatform"));
      setEditMode(true);
      setOriginalData(data);

      setDataDetails((prevData) => ({
        ...prevData,
        resolutionScreen: Number(dataToSend.screenId),
        TurnOn: dataToSend.timeTurnLampOn,
        TurnOff: dataToSend.timeTurnLampOff,
        ...(newStatusId && { IdEstatus: newStatusId }),
        coordinates: data.coordinates || prevData.coordinates,
      }));

    } else {
      showError(translate("dashboard.packages.details.dropOff.receivePackage.NOT_FOUND"));
    }
    setIsLoading(false);

  }

    useEffect(() => {
      if (typeView === 'edit' && dataDetails) {
        // Si ya existen settings, modo lectura; si no, modo edición para poder crearlos
        const hasSettings = !!dataDetails?.BoxitSettingId;
        setEditMode(hasSettings);
        setOriginalData(dataDetails);
        reset(dataDetails);
        return;
      }
      if (typeView === 'create') {
        setEditMode(false);
        return;
      }
  
    }, [typeView]);

  return (
    <form className='cardInternal' onSubmit={handleSubmit(dataDetails?.BoxitSettingId ? submitUpdateSettingsPlatform : submitCreateSettingsPlatform)}>
      <div className="cardInternal-header">
        <div className='cardInternal-title'>
          <span className="mr-1"><FaTools /></span>
          {translate("global.configuration")}
        </div>
        <div className="cardInternal-actions" style={{display:"flex",alignItems:"center", gap:'15px'}}>
        {canSyncDetails && dataDetails?.BoxitSettingId > 0 && <BtnSync mode="settings" settingId={dataDetails?.BoxitSettingId}/> }
          {
            !isLoadingData && (
              dataDetails && dataDetails?.BoxitSettingId ? (
                <div className="flex items-center" style={{gap:'10px'}}>
 {
                editMode ? (
              canUpdate && (
                <Button variant="ghostOutline" size="sm" leftIcon={<FaEdit />} type='button' onClick={() => setEditMode((prevMode) => !prevMode)}>
                  {translate("global.edit")} 
                </Button>
              )
                ):(
                  <>
                    <Button variant="ghostOutline" size="sm" type='button' onClick={() => {
                      reset(originalData);
                      setEditMode((prevMode) => !prevMode);
                      }} >
                     {translate("global.cancel")} 
                  </Button>
                  <Button variant="primary" size="sm" leftIcon={<FaSave />} type='submit' loading={isLoading} disabled={!canEditThisView}>
                   {translate("global.save")} 
                  </Button>
                  </>
                )
              }

{editMode && <Button variant="ghostOutline" size="sm" rightIcon={<FaArrowCircleRight />} onClick={() => setOptionSelect('modules')}>
                {translate("login.signup.btn.next")}
              </Button>}
                </div>
                              
              ) : (
                <Button variant="primary" size="sm" leftIcon={<FaSave />} disabled={isLoading || !canEditThisView} loading={isLoading} type="submit">
                {translate("global.save")}
              </Button>
              )
            ) 
          }
        </div>
      </div>
      <div className="cardInternal-body">
        <div className="grid grid-cols-12 gap-x-4">
          {formFields.map((field) => renderInputField(field))}
          {selectFields.map((field) => renderSelectField(field))}
          {typeView === 'edit' && (
            <div className="col-span-12">
              {isLoadingData ? <div className='mt-1 mb-2'><Skeleton width={100} height={10} /></div> : <label className="text-label-title">UUID</label>}
              <div className="mb-3 mb-2">
                {isLoadingData ? <div className='mt-2 mb-3'><Skeleton width='100%' height={40} /></div> : (
                  <input className="h-11 rounded-lg border border-transparent bg-muted/60 text-sm w-full px-3 text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:border-primary focus-visible:bg-background focus-visible:ring-4 focus-visible:ring-primary/15 disabled:bg-transparent disabled:text-muted-foreground disabled:cursor-not-allowed" value={dataDetails?.uuid || ''} disabled readOnly style={{ letterSpacing: '0.03em' }} />
                )}
              </div>
            </div>
          )}
          <div className="col-span-12">
            {isLoadingData ? <div className='mt-1 mb-2'><Skeleton width={100} height={10} /></div> : <label className="text-label-title">{translate("global.coordinates")}</label>}
            <div className="mb-3 mb-2">
              {isLoadingData ? <div className='mt-2 mb-3'><Skeleton width='100%' height={40} /></div> : (
                  <input
                  id="coordinates"
                  name="coordinates"
                  defaultValue={dataDetails?.coordinates ?? ''}
                  placeholder="40.7266465,-73.9867623"
                  disabled={!!dataDetails?.BoxitSettingId && editMode}
                  ref={register({ required: false })}
                  className="h-11 rounded-lg border border-transparent bg-muted/60 text-sm w-full px-3 text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:border-primary focus-visible:bg-background focus-visible:ring-4 focus-visible:ring-primary/15 disabled:bg-transparent disabled:text-muted-foreground disabled:cursor-not-allowed"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </form>
  );
};

export default FormSettingsLocker;
